import React, { useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Button } from "components/ui/button"
import { CalendarIcon, DownloadIcon, ChevronLeftIcon, ChevronRightIcon, ChevronsLeftIcon, ChevronsRightIcon, Users, ShoppingCart, Star, HelpCircle, Clock, BarChart3, MessageSquare, ShieldAlert, LayoutGrid, HeartPulse, Mic } from 'lucide-react'
import { AppContext } from 'contexts/AppContext';
import { Divider } from 'components/catalyst/divider'
import StatCard from 'components/ui/stat-card'
import FeedbackPlaceholder from 'components/ui/feedback-placeholder'
import BarChartPlaceholder from 'components/ui/bar-chart-placeholder'
import LineChartPlaceholder from 'components/ui/line-chart-placeholder'
import PieChartPlaceholder from 'components/ui/pie-chart-placeholder'
import StackedBarChartPlaceholder from 'components/ui/stacked-bar-chart-placeholder'
import { useMonitoringData } from 'hooks/useMonitoringData'
import DataTable from './table/data-table'
import DataTableWrapper from './table/data-table-wrapper'
import DataTableToolbar from './table/data-table-toolbar'
import SessionsTab from './SessionsTab'
import QualityTab from './QualityTab'
import OutcomesTab from './OutcomesTab'
import OverviewTab from './OverviewTab'
import AgentHealthTab from './AgentHealthTab'
import VoiceTab from './VoiceTab'
import DateRangePresets from 'components/layout/DateRangePresets'
import TermsLegend from 'components/monitoring/TermsLegend'

/**
 * Tabs hidden from the nav but NOT deleted.
 *
 * Users, Messages and Advanced Analytics are superseded by Sessions, Overview,
 * Outcomes and Quality -- except for three things that have no home yet:
 *
 *   branch_code        populated on 85,975 rows (84%). Nothing in the new
 *                      design surfaces it, and for a distributor "which branch
 *                      is asking" is a real question.
 *   account_type       customer segmentation, unsurfaced.
 *   Pain Point Clustering  a live panel in Advanced Analytics, and the redesign
 *                      lists it under Quality as an existing feature to carry
 *                      over. It has not been carried over.
 *
 * Also still only here: the testing-user exclusion behind "Table shows all
 * users including testing, tabs/stats exclude testing users" (below). Internal
 * accounts are a measured 26.6% of traffic, so that logic is load-bearing.
 *
 * Hiding rather than deleting keeps all of it recoverable by removing a single
 * array entry, and makes the retirement reversible if someone asks for a number
 * only the old tables can answer.
 */
// Analytics is gone, not hidden: the backend stopped registering /analytics
// (monitor-backend #35 -- the sweep confined ~15 defects to that stack), so a
// tab for it would render nothing but errors. Users/Messages stay legacy-only;
// they are still the sole home of branch_code and account_type.
const LEGACY_TABS = ["Users", "Messages"];

/**
 * `?legacy=1` brings them back for the session.
 *
 * Without an escape hatch "hidden" is just deleted with extra steps -- the nav
 * button is gone AND the redirect below closes the localStorage route, so there
 * would be no way to answer a question only the old tables can answer without
 * editing source and rebuilding.
 *
 * A query parameter rather than a setting: nobody arrives here by accident, it
 * needs no storage, and it disappears the moment the tab is closed.
 */
const legacyUnlocked = () => {
  try {
    return new URLSearchParams(window.location.search).get("legacy") === "1";
  } catch {
    return false;
  }
};

const HIDDEN_TABS = legacyUnlocked() ? [] : LEGACY_TABS;

export default function MonitoringPage() {
  const { user } = useAuth0();
  const { messagesData, selectedClient, userData, activeTab, setActiveTab, dateRange, accountTypeFilter, isInternal } = useContext(AppContext)

  // A persisted activeTab pointing at a hidden tab would render the legacy
  // table with no button to navigate away from it.
  useEffect(() => {
    if (HIDDEN_TABS.includes(activeTab)) setActiveTab("Overview");
  }, [activeTab, setActiveTab]);

  const [columns, setColumns] = useState([]);
  const [tableInstance, setTableInstance] = useState(null);

  
  // State to force table refresh while maintaining filters
  const [tableKey, setTableKey] = useState(0);
  
  // Store table state to persist across tab switches
  const [tableState, setTableState] = useState({
    sorting: [],
    columnFilters: [],
    globalFilter: "",
    columnVisibility: {},
    rowSelection: {}
  });

  // Use centralized data aggregation hook
  const {
    statisticsData,
    weeklyPerformanceData,
    dailyMessageCounts,
    dailyUserCounts,
    queryCategories,
    feedbackData,
    filteredMessagesData,
    filteredUserData,
    analysisMessagesData,
    analysisUserData,
    loading,
    refreshData
  } = useMonitoringData();

  // Debug effect to track statistics data changes
  useEffect(() => {
    console.log('📊 MonitoringPage - statisticsData updated:', {
      totalUsers: statisticsData.totalUsers,
      avgQuestionsPerUser: statisticsData.avgQuestionsPerUser,
      avgResponseTime: statisticsData.avgResponseTime,
      internalUsers: statisticsData.internalUsers,
      externalEnglishUsers: statisticsData.externalEnglishUsers,
      externalSpanishUsers: statisticsData.externalSpanishUsers,
      timestamp: new Date().toISOString()
    });
  }, [statisticsData]);

  useEffect(() => {
    if (!selectedClient) return; 

    const fetchColumns = async () => {
      try {
        const { messages, users } = await import(`./columns/${selectedClient.key}.js`);
        if (activeTab=='Messages') { setColumns(messages)}
        if (activeTab=='Users') { setColumns(users)}
      } catch (error) {
        console.error(`Error loading columns for client: ${selectedClient}`, error);
        setColumns([]); 
      }
    };

    fetchColumns();
  }, [activeTab, selectedClient]);

  // Force table refresh when switching tabs while preserving relevant state
  useEffect(() => {
    if (columns.length > 0) {
      // Increment key to force DataTable re-render
      setTableKey(prev => prev + 1);
      
      // Clear row selection when switching tabs since rows are different
      setTableState(prev => ({
        ...prev,
        rowSelection: {}
      }));
    }
  }, [activeTab, columns]);

  // Note: Date filtering is now handled entirely by the backend
  // When dateRange changes, the backend re-fetches data with the new date range
  // No need to apply additional frontend date filtering

  // Memoize current data to prevent unnecessary re-renders
  const currentData = useMemo(() => {
    const data = activeTab === "Messages" ? filteredMessagesData : filteredUserData;
    const analysisData = activeTab === "Messages" ? analysisMessagesData : analysisUserData;
    console.log('Index.js currentData calculation:', {
      activeTab,
      accountTypeFilter,
      tableDataLength: data.length, // Includes testing users for display
      analysisDataLength: analysisData.length, // Excludes testing users for stats
      testingUsersCount: data.length - analysisData.length,
      note: 'Table shows all users including testing, tabs/stats exclude testing users'
    });
    return data;
  }, [activeTab, filteredMessagesData, filteredUserData, analysisMessagesData, analysisUserData, accountTypeFilter]);

  // Handle tab switching with state preservation
  const handleTabSwitch = useCallback((tab) => {
    // Save current table state before switching
    if (tableInstance) {
      // Extract date filter from current table
      const currentFilters = tableInstance.getState().columnFilters;
      const dateFilter = currentFilters.find(filter => 
        filter.id === 'time_stamp' || filter.id === 'created_at'
      );
      
      // Keep non-date filters
      const nonDateFilters = currentFilters.filter(filter => 
        filter.id !== 'time_stamp' && filter.id !== 'created_at'
      );
      
      setTableState({
        sorting: tableInstance.getState().sorting,
        columnFilters: nonDateFilters, // Only keep non-date filters
        globalFilter: tableInstance.getState().globalFilter,
        columnVisibility: tableInstance.getState().columnVisibility,
        rowSelection: {}, // Clear row selection when switching tabs
        dateFilterValue: dateFilter ? dateFilter.value : null // Store date filter value separately
      });
    }
    
    setActiveTab(tab);
  }, [tableInstance, setActiveTab]);

  // Apply date filter when table is ready
  const handleTableReady = useCallback((table) => {
    setTableInstance(table);
    
    console.log('Table ready - current state:', {
      accountTypeFilter,
      tableFilters: table.getState().columnFilters,
      globalFilter: table.getState().globalFilter
    });
    
    // Clear any existing filters first to ensure clean state
    table.resetColumnFilters();
    
    // Reapply only non-date related stored state
    if (tableState.sorting.length > 0) {
      table.setSorting(tableState.sorting);
    }
    // Only apply non-date column filters
    if (tableState.columnFilters.length > 0) {
      const nonDateFilters = tableState.columnFilters.filter(filter => 
        filter.id !== 'time_stamp' && filter.id !== 'created_at'
      );
      if (nonDateFilters.length > 0) {
        table.setColumnFilters(nonDateFilters);
      }
    }
    if (tableState.globalFilter) {
      table.setGlobalFilter(tableState.globalFilter);
    }
    if (Object.keys(tableState.columnVisibility).length > 0) {
      table.setColumnVisibility(tableState.columnVisibility);
    }
    
    console.log('Table ready - after setup:', {
      tableFilters: table.getState().columnFilters,
      globalFilter: table.getState().globalFilter,
      totalRows: table.getRowModel().rows.length,
      filteredRows: table.getFilteredRowModel().rows.length
    });
    
    // Note: Date filtering is handled by the backend, so we don't apply additional date filters here
    // The filteredMessagesData and filteredUserData are already filtered by date and account type
  }, [tableState, accountTypeFilter]);


  // Helper function to format dates consistently with data processing
  const formatDateForDisplay = (date) => {
    if (!date) return '';
    // Use the same approach as data processing to avoid timezone issues
    const isoDate = date.toISOString().split('T')[0]; // YYYY-MM-DD
    const [year, month, day] = isoDate.split('-');
    const dateObj = new Date(Number(year), Number(month) - 1, Number(day));
    return dateObj.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: dateRange[0] && dateRange[1] && 
            dateRange[0].getFullYear() !== dateRange[1].getFullYear() ? 'numeric' : undefined
    });
  };

  const headerConfig = useMemo(() => {
    if (activeTab === "Users") {
      return {
        title: "Chat Agent Users",
        subtitle: `Unique users interacting with the ${selectedClient?.label ?? ""} chat agent`,
      };
    }
    if (activeTab === "Messages") {
      return {
        title: "Chat Agent Messages",
        subtitle: `Total messages exchanged via the ${selectedClient?.label ?? ""} chat agent`,
      };
    }
    if (activeTab === "Quality") {
      return {
        title: "Quality",
        subtitle: "Where the agent fails, and how often — with the reason each unmeasurable figure is unavailable",
      };
    }
    if (activeTab === "Agent Health") {
      return {
        title: "Agent Health",
        // The backlog card is internal-only, so the subtitle must not promise it
        // to everyone -- a heading describing content the reader cannot see reads
        // as something being withheld, which is worse than not mentioning it.
        subtitle: isInternal
          ? "Service status, tool reliability, and the tracking still to be added"
          : "Service status and tool reliability",
      };
    }
    if (activeTab === "Overview") {
      return {
        title: "Overview",
        subtitle: "Is it used, and for what — volume, speed, and what the assistant spends its time doing",
      };
    }
    if (activeTab === "Outcomes") {
      return {
        title: "Outcomes",
        subtitle: "Did conversations lead anywhere — what was shown, what was added, where people opened it",
      };
    }
    if (activeTab === "Sessions") {
      return {
        title: "Sessions",
        subtitle: "Read a conversation end to end — every turn, with latency, tools and feedback",
      };
    }
    if (activeTab === "Voice") {
      return {
        title: "Voice",
        subtitle: "Does hands-free voice actually work — sessions, connection time, interruptions, tokens",
      };
    }
    // The fallback names the SELECTED client, never a hardcoded one: this string
    // read "for HeritagePool+" on every tab it served, whichever vertical was
    // chosen -- and Voice landed here for lack of its own header, so the Voice
    // tab under Heritage+ was captioned as Pool data.
    return {
      title: "Chat Agent Logs",
      subtitle: `Chat agent logs for ${selectedClient?.label ?? ""}`,
    };
  }, [activeTab, isInternal, selectedClient]);

  return (
    <div className="w-full border-b border-zinc-950/10 pb-6 dark:border-white/10 pt-4">
      <div className="mb-6">
        <h2 className="text-xl font-bold">{headerConfig.title}</h2>
        <p className="text-gray-600 mt-2">{headerConfig.subtitle}</p>
      </div>

      {/* Tab Navigation */}
        {legacyUnlocked() && (
          <div className="mb-3 text-xs text-amber-800 dark:text-amber-200">
            Legacy tabs unlocked via <code>?legacy=1</code>. They are hidden by default —
            superseded, but still the only place branch_code, account_type and pain-point
            clustering appear.
          </div>
        )}
      <div className="flex space-x-4 mb-6">
        {!HIDDEN_TABS.includes("Users") && (
        <Button variant={activeTab === "Users" ? "default" : "outline"} onClick={() => handleTabSwitch("Users")}>
          Users {analysisUserData.length || ''}
        </Button>
        )}
        {!HIDDEN_TABS.includes("Messages") && (
        <Button variant={activeTab === "Messages" ? "default" : "outline"} onClick={() => handleTabSwitch("Messages")}>
          Messages {analysisMessagesData.length || ''}
        </Button>
        )}
        <Button variant={activeTab === "Overview" ? "default" : "outline"} onClick={() => handleTabSwitch("Overview")}>
          <LayoutGrid className="h-4 w-4 mr-2" />
          Overview
        </Button>
        <Button variant={activeTab === "Sessions" ? "default" : "outline"} onClick={() => handleTabSwitch("Sessions")}>
          <MessageSquare className="h-4 w-4 mr-2" />
          Sessions
        </Button>
        <Button variant={activeTab === "Outcomes" ? "default" : "outline"} onClick={() => handleTabSwitch("Outcomes")}>
          <ShoppingCart className="h-4 w-4 mr-2" />
          Outcomes
        </Button>
        <Button variant={activeTab === "Quality" ? "default" : "outline"} onClick={() => handleTabSwitch("Quality")}>
          <ShieldAlert className="h-4 w-4 mr-2" />
          Quality
        </Button>
        <Button variant={activeTab === "Agent Health" ? "default" : "outline"} onClick={() => handleTabSwitch("Agent Health")}>
          <HeartPulse className="h-4 w-4 mr-2" />
          Agent Health
        </Button>
        <Button variant={activeTab === "Voice" ? "default" : "outline"} onClick={() => handleTabSwitch("Voice")}>
          <Mic className="h-4 w-4 mr-2" />
          Voice
        </Button>
        {/* Spacer div rather than ml-auto: space-x-4's child selector outranks
            an ml-auto utility on the button itself. */}
        <div className="flex-1" />
        <TermsLegend />
      </div>

      {/* Render Advanced Analytics tab or normal content */}
      {/* The date picker lives in DataTableToolbar, which only renders on the
          table tabs below — so without this the newer tabs had no way to change
          their own range. */}
      {["Overview", "Outcomes", "Quality", "Sessions", "Agent Health", "Voice"].includes(activeTab) && (
        // Sessions reads its own platform control, and Voice sends no platform
        // at all (voice is mobile-only today -- useVoiceData.js). The global
        // buttons are hidden on both rather than shown doing nothing: they used
        // to render on Voice, highlight when clicked, and change no figure.
        <DateRangePresets
          showPlatform={!["Sessions", "Voice"].includes(activeTab)}
          showLanguage={["Overview", "Quality", "Sessions"].includes(activeTab)}
        />
      )}

      {activeTab === "Agent Health" ? (
        <AgentHealthTab />
      ) : activeTab === "Overview" ? (
        <OverviewTab />
      ) : activeTab === "Outcomes" ? (
        <OutcomesTab />
      ) : activeTab === "Quality" ? (
        <QualityTab />
      ) : activeTab === "Sessions" ? (
        <SessionsTab />
      ) : activeTab === "Voice" ? (
        <VoiceTab />
      ) : (
        <>
          {/* Data Table Toolbar - visible across tabs */}
          <div className="mb-6">
            <DataTableToolbar table={tableInstance} />
          </div>

          {/* Statistics Cards - Now using centralized data */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <StatCard 
              icon={Users} 
              title={activeTab === "Messages" ? "Total Messages" : "Total Users"}
              value={activeTab === "Messages" 
                ? `${statisticsData.totalUsers.toLocaleString()} ${accountTypeFilter ? accountTypeFilter.toLowerCase() : 'new'} messages`
                : `${statisticsData.totalUsers.toLocaleString()} ${accountTypeFilter ? accountTypeFilter.toLowerCase() : 'unique'} users`
              } 
              iconColor="text-blue-500"
              tooltipContent={
                accountTypeFilter
                  ? `Showing only ${accountTypeFilter} ${activeTab === "Messages" ? "messages" : "users"}`
                  : activeTab === "Messages"
                    ? `Internal: ${statisticsData.internalMessages?.toLocaleString() || 0}\nExternal (English): ${statisticsData.externalEnglishMessages?.toLocaleString() || 0}\nExternal (Spanish): ${statisticsData.externalSpanishMessages?.toLocaleString() || 0}`
                    : `Internal: ${statisticsData.internalUsers?.toLocaleString() || 0}\nExternal (English): ${statisticsData.externalEnglishUsers?.toLocaleString() || 0}\nExternal (Spanish): ${statisticsData.externalSpanishUsers?.toLocaleString() || 0}`
              }
            />
            <StatCard 
              icon={HelpCircle} 
              title="Avg Questions Per User" 
              value={`${statisticsData.avgQuestionsPerUser} questions`} 
              iconColor="text-purple-500"
              tooltipContent={
                accountTypeFilter 
                  ? `Calculated for ${accountTypeFilter} users only` 
                  : `Internal: ${statisticsData.avgQuestionsPerInternal}\nExternal (English): ${statisticsData.avgQuestionsPerExternalEnglish}\nExternal (Spanish): ${statisticsData.avgQuestionsPerExternalSpanish}`
              }
            />
            <StatCard 
              icon={Clock} 
              title="Avg Response Time" 
              value={`${statisticsData.avgResponseTime} seconds`} 
              iconColor="text-yellow-500"
              tooltipContent={
                accountTypeFilter 
                  ? `Calculated for ${accountTypeFilter} users only` 
                  : `Internal: ${statisticsData.avgResponseTimeInternal}s\nExternal (English): ${statisticsData.avgResponseTimeExternalEnglish}s\nExternal (Spanish): ${statisticsData.avgResponseTimeExternalSpanish}s`
              }
            />
          </div>

          {/* Chart and Feedback Section */}
          <div className="grid grid-cols-12 gap-6 mb-8 items-stretch">
            {/* Conditional rendering based on tab and client */}
            {activeTab === "Messages" && selectedClient && 
             (selectedClient.key === "heritage" || selectedClient.key === "heritagepool") ? (
              <>
                {/* Line Chart for Messages tab - Message Trends Over Time */}
                <div className="col-span-12 lg:col-span-8 flex">
                  <LineChartPlaceholder 
                    title="Message Trends Over Time"
                    subtitle={`${formatDateForDisplay(dateRange[0])} - ${formatDateForDisplay(dateRange[1])}`}
                    data={dailyMessageCounts}
                    accountTypeFilter={accountTypeFilter}
                  />
                </div>
                
                {/* Pie Chart for Messages tab - Using centralized data */}
                <div className="col-span-12 lg:col-span-4 flex">
                  <PieChartPlaceholder 
                    title="External User Query Categories"
                    data={queryCategories}
                  />
                </div>
              </>
            ) : (
              <>
                {/* Stacked Bar Chart for Users tab - Daily Unique Users */}
                <div className="col-span-12 lg:col-span-9">
                  <StackedBarChartPlaceholder
                    title={accountTypeFilter ? `Daily New ${accountTypeFilter} Users` : "Daily New Unique Users"}
                    subtitle={`${formatDateForDisplay(dateRange[0])} - ${formatDateForDisplay(dateRange[1])}`}
                    data={dailyUserCounts}
                    accountTypeFilter={accountTypeFilter}
                  />
                </div>
                
                {/* Original Feedback for Users tab - Using centralized data */}
                <div className="col-span-12 lg:col-span-3">
                  <FeedbackPlaceholder 
                    totalFeedback={feedbackData.total.toString()}
                    positiveFeedback={feedbackData.positive.toString()}
                    negativeFeedback={feedbackData.negative.toString()}
                  />
                </div>
              </>
            )}
          </div>

          {/* Data Table */}
          <DataTable
            key={tableKey} // Force re-render when switching tabs
            data={currentData}
            columns={columns || []}
            hideToolbar={true}
            onTableReady={handleTableReady}
            initialState={tableState}
          />
        </>
      )}
    </div>
  );
}
